import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';

import { ApiPaymentGatewayService } from '@services/api-payment-gateway.service';
import { SharedService } from '@services/shared.service';
import { FormBase } from '@shared/form-base.component';
import { Country } from '@shared/models/Enums';
import { PaymentGatewayModel } from '@shared/models/PaymentGatewayModel';
import { CardPaymentModel } from '@shared/models/RegisterModel';
import { StripeCardComponent } from '@components/stripe-card/stripe-card.component';


/**
 * Formulario para agregar una tarjeta.
 *
 * Es el único lugar del proyecto que captura datos de tarjeta: antes estaba copiado
 * en los seis modales de compra, en payment-method y en la pantalla de configuración.
 *
 * Según la pasarela que el backend declare para el país muestra uno de dos formularios:
 * con ONVO el número viaja al backend y él tokeniza; con Stripe la tarjeta se tokeniza
 * en el navegador y solo sale un payment method (pm_xxx) más los últimos 4 dígitos.
 * El padre no necesita saber cuál de los dos se usó: recibe siempre un CardPaymentModel
 * listo para mandar a `card/create/v1`.
 */
@Component({
    selector: 'app-card-form',
    templateUrl: './card-form.component.html',
    styleUrls: ['./card-form.component.scss']
})
export class CardFormComponent extends FormBase<any> implements OnInit {

    @Input() country: Country = Country.CR;

    // Muestra el check de "guardar para futuras compras"
    @Input() showSaveCard = false;

    // Los campos de Stripe son iframes y no heredan el CSS de la página: si la pantalla
    // que los contiene es oscura hay que avisarlo para que el texto se vea.
    @Input() isDarkMode = false;

    // Emite la tarjeta lista para enviar, o null mientras esté incompleta o inválida
    @Output() cardChange: EventEmitter<CardPaymentModel> = new EventEmitter<CardPaymentModel>();

    @ViewChild(StripeCardComponent) stripeCard: StripeCardComponent;

    cardForm: UntypedFormGroup;

    isAmexCVC = false;
    dynamicMask = '999';

    paymentGateway: PaymentGatewayModel = null;

    get isStripe(): boolean {

        return this.paymentGateway?.isStripe == true;
    }

    get cardName() { return this._formGet(this.cardForm, 'cardName'); }
    get number() { return this._formGet(this.cardForm, 'number'); }
    get expiry() { return this._formGet(this.cardForm, 'expiry'); }
    get cvc() { return this._formGet(this.cardForm, 'cvc'); }

    get invalidCardName() { return this._isInvalid(this.cardForm, 'cardName', this.touched); }
    get invalidNumber() { return this._isInvalid(this.cardForm, 'number', this.touched); }
    get invalidExpiry() { return this._isInvalid(this.cardForm, 'expiry', this.touched); }
    get invalidCvc() { return this._isInvalid(this.cardForm, 'cvc', this.touched); }

    constructor(public sharedService: SharedService,
        private formBuilder: UntypedFormBuilder,
        private apiPaymentGatewayService: ApiPaymentGatewayService) {
        super();
    }

    ngOnInit(): void {

        // El formulario depende de la pasarela del país, así que se arma después de
        // resolverla. Si la consulta falla se sigue con ONVO, el comportamiento actual.
        this.apiPaymentGatewayService.getPaymentGateway(this.country).then(
            (paymentGateway) => {
                this.paymentGateway = paymentGateway;
                this._formInit();
            },
            (err) => {
                this.paymentGateway = null;
                console.error(err);
                this._formInit();
            }
        );
    }

    /**
     * Tokeniza con Stripe.js apenas los tres campos quedan completos y guarda el
     * resultado en el formulario. Si el usuario corrige la tarjeta se vuelve a tokenizar.
     */
    async onStripeCardComplete(complete: boolean): Promise<void> {

        if (!complete) {

            this.cardForm?.patchValue({ token: '', brand: '', number: '' });
            return;
        }

        const card = await this.stripeCard?.createPaymentMethod();

        if (card == null) {

            this.cardForm?.patchValue({ token: '', brand: '', number: '' });
            return;
        }

        this.cardForm.patchValue({
            token: card.token,
            brand: card.brand,
            // Solo los últimos 4: el número completo nunca sale del iframe de Stripe
            number: card.last4
        });
    }

    onChangeCardExpiry(ev: any) {

        const val = ev.target.value;
        let temp_val = '';

        this._dynamicCardInputValidation();

        if (!isNaN(val)) {

            if (val > 1 && val < 10 && val.length == 1) {

                temp_val = '0' + val + '/';
                this.expiry.setValue(temp_val);
            } else if (val >= 1 && val < 10 && val.length == 2 && ev.keyCode != 8) {

                temp_val = val + '/';
                this.expiry.setValue(temp_val);
            } else if (val > 9 && val.length == 2 && ev.keyCode != 8) {

                temp_val = val + '/';
                this.expiry.setValue(temp_val);
            }
        }

        return (val.length < 7) || ev.which == 8;
    }

    onChangeCardNumber(ev: any) {

        const cardNumber = ev.target.value;
        const number = Number(cardNumber.substring(0, 2));

        // Amex usa CVC de 4 dígitos, así que cambia la máscara
        if (number == 34 || number == 37) {

            this.isAmexCVC = true;
            this.dynamicMask = '9999';
            this.cvc.setValue('');
        } else {

            this.dynamicMask = '999';

            if (this.isAmexCVC) this.cvc.setValue('');

            this.isAmexCVC = false;
        }

        this._dynamicCardInputValidation();
    }

    onChangeCVC(ev: any) {

        this._dynamicCardInputValidation();
    }

    private _formInit() {

        if (this.isStripe) {

            // Con Stripe el número, la expiración y el CVC viven en el iframe: acá solo
            // se guarda lo que Stripe devuelve. `number` son los últimos 4 dígitos, que
            // es lo único que el backend acepta en estos países.
            this.cardForm = this.formBuilder.group({
                cardName: new UntypedFormControl('', [Validators.required]),
                token: new UntypedFormControl('', [Validators.required]),
                brand: new UntypedFormControl(''),
                number: new UntypedFormControl(''),
                allowSavingCard: new UntypedFormControl(true)
            });
        } else {

            this.cardForm = this.formBuilder.group({
                cardName: new UntypedFormControl('', [Validators.required]),
                number: new UntypedFormControl('', [Validators.required, Validators.pattern('^(?:4[0-9]{12}(?:[0-9]{3})?|5[1-5][0-9]{14}|6(?:011|5[0-9][0-9])[0-9]{12}|3[47][0-9]{13}|3(?:0[0-5]|[68][0-9])[0-9]{11}|(?:2131|1800|35\\d{3})\\d{11})$')]),
                expiry: new UntypedFormControl('', [Validators.required]),
                cvc: new UntypedFormControl('', [Validators.required, Validators.pattern('^\\d{3}$')]),
                allowSavingCard: new UntypedFormControl(true)
            });
        }

        this.cardForm.valueChanges.subscribe(() => this._emitCard());
    }

    // El padre recibe la tarjeta ya armada y no se entera de qué pasarela hay detrás
    private _emitCard() {

        if (this.cardForm.invalid) {

            this.cardChange.emit(null);
            return;
        }

        const controls = this.cardForm.controls;

        this.cardChange.emit(new CardPaymentModel({
            cardname: (controls.cardName.value ?? '').trim(),
            number: (controls.number.value ?? '').trim(),
            expiry: (controls.expiry?.value ?? '').trim(),
            cvc: (controls.cvc?.value ?? '').trim(),
            token: controls.token?.value ?? '',
            brand: controls.brand?.value ?? '',
            allowSaveCard: controls.allowSavingCard.value
        }));
    }

    private _dynamicCardInputValidation(require = true) {

        // Con Stripe no hay campos que validar acá: los valida el iframe
        if (this.isStripe) return;

        if (require) {

            this.cardName.setValidators([Validators.required]);
            this.expiry.setValidators([Validators.required]);
            this.number.setValidators([Validators.required, Validators.pattern('^(?:4[0-9]{12}(?:[0-9]{3})?|5[1-5][0-9]{14}|6(?:011|5[0-9][0-9])[0-9]{12})$')]);
            this.cvc.setValidators([Validators.required, Validators.pattern('^\\d{3}$')]);

            if (this.number.value) {

                const firstNumber = this.number.value.substring(0, 1);
                const firstSecondNumber = Number(this.number.value.substring(0, 2));

                if (firstSecondNumber == 34 || firstSecondNumber == 37) {

                    this.number.setValidators([Validators.required, Validators.pattern('^(?:3[47][0-9]{13}|3(?:0[0-5]|[68][0-9])[0-9]{11}|(?:2131|1800|35\\d{3})\\d{11})$')]);
                    this.cvc.setValidators([Validators.required, Validators.pattern('^\\d{4}$')]);
                } else if (firstNumber != '4' && !(firstSecondNumber >= 51 && firstSecondNumber <= 55) && !(firstSecondNumber == 60)) {

                    // Otros números diferentes a Visa, MasterCard y Discovery son inválidos
                    this.cvc.setValidators([Validators.required, Validators.pattern('^\\d{3,4}$')]);
                    this.number.setValidators([Validators.required, Validators.pattern('^(?:999900008888000077770000666600005555)$')]);
                }
            }
        }

        this.cardName.updateValueAndValidity();
        this.cvc.updateValueAndValidity();
        this.number.updateValueAndValidity();
        this.expiry.updateValueAndValidity();
    }
}
